// ============================================================================
// OrganigrammePage / OrgExplorer — /organigramme (E1c, NAV v3 focus incrementale)
// ============================================================================
// NAV v3 : vue focus par defaut (manager + focus + reports), navigation de proche
// en proche, apercu + sans changer le focus, bascule vue etendue (cartes v3). Foret CLIENT ;
// backend org intact (M-217 zero ecriture).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PageHeader, EmptyState, SkeletonCard, QueryError, GsSearchInput,
  GsSegmented, GS_COMPONENTS, GS_COLORS,
} from '@gs/ui';
import type { GsSegmentedOption } from '@gs/ui';
import { ChevronRight, Crosshair, FileText, Image as ImageIcon, Network, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  buildOrgForest,
  collectPathEmails,
  filterPeopleForTeam,
  findOrgNode,
  type OrgPerson,
  type OrgTeamLike,
} from '../../lib/org/hierarchy';
import { getInitials } from '../../lib/profile/initials';
import { useOrg, useExportOrgPdf, type OrgPersonDto, type OrgResponse } from '../../hooks/useOrg';
import { OrgChart, type OrgFocus } from '../../components/org/OrgChart';
import { OrgFocusView } from '../../components/org/OrgFocusView';
import { OrgMobileList } from '../../components/org/OrgMobileList';

const ALL = 'all';
const MAX_RESULTS = 8;
type OrgViewMode = 'focus' | 'extended';

const fold = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

const slug = (s: string): string =>
  fold(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'organigramme';

function OrgForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:organigramme.forbidden.title')}
      description={t('gest:organigramme.forbidden.description')}
    />
  );
}

function toOrgPeople(data: OrgResponse): OrgPerson[] {
  const directorEmails = new Set(
    data.teams.flatMap((tm) => (tm.directorEmail ? [fold(tm.directorEmail)] : [])),
  );
  const roleOf = (p: OrgPersonDto): OrgPerson['role'] => {
    if (p.systemCRole === 'ADMIN') return 'admin';
    if (directorEmails.has(fold(p.email))) return 'director';
    if (p.systemCRole === 'GESTIONNAIRE') return 'manager';
    return 'employee';
  };
  return data.people.map((p) => ({
    email: p.email,
    full_name: p.fullName,
    job_title: p.jobTitle,
    role: roleOf(p),
    committee: p.committee === 'ce' || p.committee === 'cg' ? p.committee : null,
    team_id: p.teamId,
    manager_email: p.managerEmail,
    avatar_url: null,
    href: `/profile/${encodeURIComponent(p.email)}`,
  }));
}

function resolveInitialFocusEmail(
  roots: ReturnType<typeof buildOrgForest>['roots'],
  userEmail: string | undefined,
): string | null {
  if (roots.length === 0) return null;
  if (userEmail && collectPathEmails(roots, userEmail).length > 0) return userEmail;
  return roots[0].email;
}

export default function OrganigrammePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const orgQ = useOrg(!!user);
  const exportPdf = useExportOrgPdf();

  const [teamId, setTeamId] = useState<string>(ALL);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightedEmail, setHighlightedEmail] = useState<string | null>(null);
  const [focusedEmail, setFocusedEmail] = useState<string | null>(null);
  const [previewEmail, setPreviewEmail] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<OrgViewMode>('focus');
  const [focus, setFocus] = useState<OrgFocus | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const treeRef = useRef<HTMLDivElement>(null);
  const focusKey = useRef(0);

  const forbidden = orgQ.isError && orgQ.error instanceof ApiForbiddenError;

  const people = useMemo(() => (orgQ.data ? toOrgPeople(orgQ.data) : []), [orgQ.data]);
  const teams: OrgTeamLike[] = useMemo(
    () =>
      (orgQ.data?.teams ?? []).map((tm) => ({
        id: tm.id,
        name: tm.name,
        director_email: tm.directorEmail,
      })),
    [orgQ.data],
  );

  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [teams],
  );
  const selectedTeam = teamId === ALL ? null : teams.find((tm) => tm.id === teamId) ?? null;

  const teamFilterOptions: GsSegmentedOption[] = useMemo(
    () => [
      { value: ALL, label: t('gest:organigramme.filter.all') },
      ...sortedTeams.map((tm) => ({ value: tm.id, label: tm.name })),
    ],
    [sortedTeams, t],
  );

  const viewModeOptions: GsSegmentedOption[] = useMemo(
    () => [
      { value: 'focus', label: t('gest:organigramme.view.focus') },
      { value: 'extended', label: t('gest:organigramme.view.extended') },
    ],
    [t],
  );

  const viewPeople = useMemo(
    () => (selectedTeam ? filterPeopleForTeam(people, selectedTeam) : people),
    [people, selectedTeam],
  );
  const forest = useMemo(() => buildOrgForest(viewPeople, teams), [viewPeople, teams]);

  const focusPath = useMemo(
    () => (focusedEmail ? collectPathEmails(forest.roots, focusedEmail) : []),
    [focusedEmail, forest.roots],
  );

  const breadcrumbNodes = useMemo(
    () =>
      focusPath
        .map((email) => findOrgNode(forest.roots, email))
        .filter((n): n is NonNullable<typeof n> => n !== null),
    [focusPath, forest.roots],
  );

  const results = useMemo(() => {
    const q = fold(query);
    if (!q) return [];
    return people.filter((p) => fold(p.full_name ?? p.email).includes(q)).slice(0, MAX_RESULTS);
  }, [people, query]);

  useEffect(() => {
    if (forest.roots.length === 0) {
      setFocusedEmail(null);
      return;
    }
    setFocusedEmail((prev) => {
      if (prev && collectPathEmails(forest.roots, prev).length > 0) return prev;
      return resolveInitialFocusEmail(forest.roots, user?.email);
    });
    setPreviewEmail(null);
  }, [forest.roots, user?.email, teamId]);

  const focusOn = useCallback(
    (email: string) => {
      const path = collectPathEmails(forest.roots, email);
      if (path.length === 0) return;
      setFocusedEmail(email);
      setHighlightedEmail(email);
      setPreviewEmail(null);
      setViewMode('focus');
      focusKey.current += 1;
      setFocus({ email, key: focusKey.current });
    },
    [forest.roots],
  );

  function selectPerson(p: OrgPerson) {
    const inView = viewPeople.some((v) => v.email === p.email);
    if (!inView) setTeamId(ALL);
    setQuery(p.full_name ?? p.email);
    setSearchOpen(false);
    focusOn(p.email);
  }

  function handleTeamFilterChange(nextTeamId: string) {
    setTeamId(nextTeamId);
    setHighlightedEmail(null);
    setPreviewEmail(null);
  }

  function navigateBreadcrumb(index: number | null) {
    if (index === null) {
      const root = forest.roots[0];
      if (root) setFocusedEmail(root.email);
    } else {
      setFocusedEmail(focusPath[index]);
    }
    setHighlightedEmail(null);
    setPreviewEmail(null);
  }

  function goToMyPosition() {
    if (!user?.email) return;
    focusOn(user.email);
  }

  async function capturePng(): Promise<string | null> {
    const el = treeRef.current;
    if (!el) return null;
    const canvas = el.closest('[data-org-canvas]') as HTMLElement | null;
    const backgroundColor = canvas ? getComputedStyle(canvas).backgroundColor : GS_COLORS.white;
    const { toPng } = await import('html-to-image');
    return toPng(el, { pixelRatio: 2, backgroundColor, cacheBust: true });
  }

  function triggerDownload(href: string, filename: string) {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const suffix = selectedTeam ? `-${slug(selectedTeam.name)}` : '';

  async function exportPng() {
    setExporting(true);
    setExportError(null);
    try {
      const dataUrl = await capturePng();
      if (!dataUrl) return;
      triggerDownload(dataUrl, `organigramme${suffix}.png`);
    } catch {
      setExportError('png');
    } finally {
      setExporting(false);
    }
  }

  async function exportPdfFile() {
    setExporting(true);
    setExportError(null);
    let url: string | null = null;
    try {
      const image = await capturePng();
      if (!image) return;
      const blob = await exportPdf.mutateAsync({ image, teamId: selectedTeam?.id ?? null });
      url = URL.createObjectURL(blob);
      triggerDownload(url, `organigramme${suffix}.pdf`);
    } catch {
      setExportError('pdf');
    } finally {
      if (url) URL.revokeObjectURL(url);
      setExporting(false);
    }
  }

  const canExport = !!orgQ.data && forest.roots.length > 0;
  const showOrgContent = focusedEmail != null && forest.roots.length > 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:organigramme.title')}
        description={
          orgQ.data
            ? t('gest:organigramme.subtitle', {
                teams: orgQ.data.teams.length,
                people: orgQ.data.people.length,
              })
            : undefined
        }
        onRefresh={() => void orgQ.refetch()}
        refreshLoading={orgQ.isFetching}
      />

      {exportError ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {t(`gest:organigramme.errors.${exportError === 'pdf' ? 'exportPdf' : 'exportPng'}`)}
        </div>
      ) : null}

      {forbidden ? (
        <OrgForbidden />
      ) : orgQ.isLoading ? (
        <SkeletonCard />
      ) : orgQ.isError ? (
        <QueryError message={t('gest:organigramme.loadError')} onRetry={() => void orgQ.refetch()} />
      ) : orgQ.data ? (
        <>
          {breadcrumbNodes.length > 0 ? (
            <nav
              aria-label={t('gest:organigramme.breadcrumbLabel')}
              className="flex items-center gap-1.5 overflow-x-auto text-meta text-slate-500 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <button
                type="button"
                onClick={() => navigateBreadcrumb(null)}
                className="touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
              >
                {t('gest:organigramme.title')}
              </button>
              {breadcrumbNodes.map((node, index) => (
                <span key={node.email} className="inline-flex shrink-0 items-center gap-1.5">
                  <ChevronRight size={14} aria-hidden="true" className="text-slate-300 dark:text-slate-600" />
                  {index === breadcrumbNodes.length - 1 ? (
                    <span aria-current="page" className="truncate font-semibold text-slate-800 dark:text-slate-100">
                      {node.full_name ?? node.email}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigateBreadcrumb(index)}
                      className="touch-manipulation truncate font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
                    >
                      {node.full_name ?? node.email}
                    </button>
                  )}
                </span>
              ))}
            </nav>
          ) : null}

          <div className="flex flex-col gap-3">
            <GsSegmented
              variant="scrollable"
              ariaLabel={t('gest:organigramme.filter.label')}
              options={teamFilterOptions}
              value={teamId}
              onChange={handleTeamFilterChange}
              className="pb-1"
            />

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div
                className="relative w-full sm:max-w-xs"
                onFocusCapture={() => setSearchOpen(true)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSearchOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && results[0]) {
                    e.preventDefault();
                    selectPerson(results[0]);
                  } else if (e.key === 'Escape') {
                    setSearchOpen(false);
                  }
                }}
              >
                <GsSearchInput
                  variant="pill"
                  className="w-full"
                  value={query}
                  onChange={(v) => {
                    setQuery(v);
                    setSearchOpen(true);
                  }}
                  placeholder={t('gest:organigramme.search.placeholder')}
                />
                {searchOpen && results.length > 0 ? (
                  <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    {results.map((p) => (
                      <li key={p.email}>
                        <button
                          type="button"
                          onClick={() => selectPerson(p)}
                          className="flex w-full touch-manipulation items-center gap-2 px-2.5 py-1.5 text-left text-field hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <span
                            aria-hidden="true"
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-chip font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                          >
                            {getInitials(p.full_name, p.email)}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">
                            {p.full_name ?? p.email}
                            {p.job_title ? (
                              <span className="text-slate-500 dark:text-slate-400"> · {p.job_title}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <GsSegmented
                  ariaLabel={t('gest:organigramme.view.label')}
                  options={viewModeOptions}
                  value={viewMode}
                  onChange={(v) => setViewMode(v as OrgViewMode)}
                />
                <button
                  type="button"
                  onClick={goToMyPosition}
                  disabled={!user?.email}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
                >
                  <Crosshair size={16} aria-hidden="true" />
                  {t('gest:organigramme.focus.myPosition')}
                </button>
                <button
                  type="button"
                  onClick={() => void exportPng()}
                  disabled={exporting || !canExport}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
                >
                  <ImageIcon size={16} aria-hidden="true" />
                  {t('gest:organigramme.export.png')}
                </button>
                <button
                  type="button"
                  onClick={() => void exportPdfFile()}
                  disabled={exporting || !canExport}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
                >
                  <FileText size={16} aria-hidden="true" />
                  {t('gest:organigramme.export.pdf')}
                </button>
              </div>
            </div>
          </div>

          {orgQ.data.people.length === 0 ? (
            <EmptyState
              icon={<Network size={48} aria-hidden="true" />}
              title={t('gest:organigramme.empty')}
            />
          ) : showOrgContent ? (
            <>
              {viewMode === 'focus' ? (
                <>
                  <OrgMobileList
                    roots={forest.roots}
                    focusedEmail={focusedEmail}
                    highlightedEmail={highlightedEmail}
                    previewEmail={previewEmail}
                    onFocus={focusOn}
                    onPreviewToggle={setPreviewEmail}
                  />
                  <div className="hidden md:block">
                    <OrgFocusView
                      roots={forest.roots}
                      focusedEmail={focusedEmail}
                      highlightedEmail={highlightedEmail}
                      previewEmail={previewEmail}
                      onFocus={focusOn}
                      onPreviewToggle={setPreviewEmail}
                    />
                  </div>
                </>
              ) : (
                <OrgChart
                  roots={forest.roots}
                  highlightedEmail={highlightedEmail}
                  focusedEmail={focusedEmail}
                  focus={focus}
                  onDrillIn={focusOn}
                />
              )}

              {/* Arbre complet hors-ecran pour export PNG/PDF (toujours la foret entiere). */}
              <div
                aria-hidden="true"
                className="pointer-events-none fixed left-[-9999px] top-0 -z-10 opacity-0"
              >
                <OrgChart ref={treeRef} roots={forest.roots} onDrillIn={focusOn} />
              </div>
            </>
          ) : null}

          {teamId === ALL && forest.unattached.length > 0 ? (
            <section className={`${GS_COMPONENTS.card} p-4`}>
              <h2 className="text-field font-semibold text-slate-900 dark:text-slate-100">
                {t('gest:organigramme.unattached.title')}
              </h2>
              <p className="mt-0.5 text-chip text-slate-500 dark:text-slate-400">
                {t('gest:organigramme.unattached.description')}
              </p>
              <ul className="mt-3 flex flex-col gap-2">
                {forest.unattached.map((node) => (
                  <li key={node.email} className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-chip font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                    >
                      {getInitials(node.full_name, node.email)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="block truncate text-field text-slate-900 dark:text-slate-100">
                        {node.full_name ?? node.email}
                      </span>
                      <span className="block truncate text-chip text-slate-500 dark:text-slate-400">
                        {node.job_title ?? node.email}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
